import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../api/resources.js';
import { session } from '../../api/client.js';
import { Button } from '../../components/ui/button.js';
import { useAction, useAsync } from '../../lib/hooks.js';
import { CodeInput } from './code-input.js';
import { AuthError, AuthLayout } from './layout.js';

const RESEND_SECONDS = 60;

/**
 * Gate de cadastro (docs/mega-panel.md): a conta existe, mas até o código confirmar o
 * e-mail o console não deixa colocar um número no ar. O reenvio conta 60 segundos aqui
 * e no servidor — a contagem daqui é cortesia; a de lá é a regra.
 */
export function VerifyEmailPage() {
  const navigate = useNavigate();
  const me = useAsync(() => auth.me(), []);
  const [code, setCode] = useState('');
  const [cooldown, setCooldown] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (me.data?.emailVerified) navigate('/', { replace: true });
  }, [me.data, navigate]);

  const verify = useAction(async () => {
    await auth.verifyEmail(code);
    navigate('/', { replace: true });
  });

  const resend = useAction(async () => {
    try {
      await auth.resendEmailCode();
      setCooldown(RESEND_SECONDS);
    } catch (error) {
      const retry = (error as { details?: { retryAfterSeconds?: number } }).details
        ?.retryAfterSeconds;
      if (retry) setCooldown(retry);
      throw error;
    }
  });

  const logout = async () => {
    await auth.logout().catch(() => undefined);
    session.clear();
    navigate('/login', { replace: true });
  };

  return (
    <AuthLayout
      title="Confirme seu e-mail"
      description={
        <>
          Enviamos um código de 6 dígitos para{' '}
          <strong className="text-foreground">{me.data?.user?.email ?? 'seu e-mail'}</strong>. Ele
          vale por 15 minutos.
        </>
      }
      footer={
        <button type="button" className="link" onClick={() => void logout()}>
          Sair e entrar com outra conta
        </button>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void verify.run();
        }}
      >
        <AuthError error={verify.error ?? resend.error} />
        <CodeInput value={code} onChange={setCode} autoFocus />
        <Button className="mt-5 w-full" type="submit" disabled={verify.pending || code.length < 6}>
          {verify.pending ? 'Confirmando…' : 'Confirmar e-mail'}
        </Button>
        <div className="mt-3 text-center text-sm text-muted-foreground">
          {cooldown > 0 ? (
            <span>Reenviar código em {cooldown}s</span>
          ) : (
            <button
              type="button"
              className="link"
              disabled={resend.pending}
              onClick={() => void resend.run()}
            >
              Reenviar código
            </button>
          )}
        </div>
      </form>
    </AuthLayout>
  );
}
